import {
  AuthenticateWithRedirectCallback,
  SignedIn,
  SignedOut,
  useAuth,
  useSignIn,
} from "@clerk/clerk-react";
import {
  ChartLine,
  Copy,
  ExternalLink,
  Funnel,
  Link2,
  ListTodo,
  LogOut,
  Menu,
  MessageCircle,
  Plus,
  RefreshCw,
  ShieldCheck,
  Users,
  X,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { AccountRoles } from "./access/AccountRoles";
import { apiRequest } from "./api";
import { MarketingOverview } from "./marketing/Overview";
import { percent, total } from "./metrics";
import { Logo } from "./shell/Logo";
import { TaskManagement } from "./tasks/TaskManagement";
import type { BootstrapResponse, FunnelRow, LineMessage, TrackingLink } from "./types";

type PageKey = "marketing" | "measurement" | "tracking" | "line" | "finance" | "illume" | "tasks" | "tasksTimeline" | "access";

// 当面は集客だけを公開する。売上・illume・タスク管理は再開時にここへ戻す（2026-09-23）。
const PUBLISHED_PAGES: PageKey[] = ["marketing", "measurement", "tracking", "line", "access"];

const pagePath: Record<PageKey, string> = {
  marketing: "/marketing",
  measurement: "/marketing/funnel",
  tracking: "/marketing/tracking-links",
  line: "/marketing/line",
  finance: "/finance",
  illume: "/illume",
  tasks: "/tasks/board",
  tasksTimeline: "/tasks/timeline",
  access: "/access",
};

const pageMeta: Record<PageKey, { label: string; section: string }> = {
  marketing: { label: "集客概要", section: "集客" },
  measurement: { label: "ファネル", section: "集客" },
  tracking: { label: "計測URL", section: "集客" },
  line: { label: "連携の状態", section: "LINE" },
  finance: { label: "売上", section: "売上" },
  illume: { label: "illume", section: "illume" },
  tasks: { label: "ボード", section: "タスク管理" },
  tasksTimeline: { label: "タイムライン", section: "タスク管理" },
  access: { label: "アカウントと権限", section: "設定" },
};

function currentPage(): PageKey {
  const page = requestedPage();
  return PUBLISHED_PAGES.includes(page) ? page : "marketing";
}

function requestedPage(): PageKey {
  const legacyPaths: Record<string, PageKey> = {
    "/": "marketing",
    "/measurement": "measurement",
    "/tracking-links": "tracking",
    "/line": "line",
    "/home": "marketing",
    "/announcements": "marketing",
    "/tasks": "tasks",
  };
  const legacy = legacyPaths[location.pathname];
  if (legacy) return legacy;
  const match = Object.entries(pagePath)
    .filter(([, path]) => path !== "/")
    .sort(([, a], [, b]) => b.length - a.length)
    .find(([, path]) => location.pathname.startsWith(path));
  return (match?.[0] as PageKey | undefined) ?? "marketing";
}

function LineLogin() {
  const { isLoaded, signIn } = useSignIn();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function login() {
    if (!isLoaded || !signIn) return;
    setLoading(true);
    setError(null);
    try {
      const isInvitation = location.pathname.startsWith("/invite/");
      await signIn.authenticateWithRedirect({
        strategy: "oauth_line",
        redirectUrl: "/sso-callback",
        redirectUrlComplete: isInvitation ? `${location.pathname}${location.search}` : "/",
      });
    } catch {
      setError("LINEログインを開始できませんでした。もう一度お試しください。");
      setLoading(false);
    }
  }

  return (
    <main className="login-page">
      <header className="login-masthead"><Logo className="login-logo" /><span>INTERNAL USE ONLY</span></header>
      <section className="login-auth" aria-label="ログイン">
        <div className="login-auth-inner">
          <p className="login-step">ACCESS / 01</p>
          <h1 id="login-title">運営管理</h1>
          <p className="muted">集客の数字とLINE連携を扱う社内システムです。許可されたLINEアカウントでログインしてください。</p>
          <button className="button button-line button-wide" onClick={login} disabled={!isLoaded || loading}>
            <MessageCircle size={19} aria-hidden="true" />
            {loading ? "LINEに接続中…" : "LINEでログイン"}
          </button>
          {error && <p className="error-text" role="alert">{error}</p>}
          <p className="login-note"><ShieldCheck size={16} aria-hidden="true" /> ロールに応じて閲覧・操作範囲を制御します</p>
        </div>
      </section>
      <footer className="login-footer"><span>design me</span><span>SECURE ACCESS</span></footer>
    </main>
  );
}

function App() {
  if (location.pathname === "/sso-callback") {
    return <AuthenticateWithRedirectCallback />;
  }
  return (
    <>
      <SignedOut><LineLogin /></SignedOut>
      <SignedIn><AuthenticatedApp /></SignedIn>
    </>
  );
}

function AuthenticatedApp() {
  const { getToken, signOut } = useAuth();
  const [bootstrap, setBootstrap] = useState<BootstrapResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [invitationState, setInvitationState] = useState<"idle" | "accepting" | "error">("idle");
  const [invitationError, setInvitationError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setBootstrap(await apiRequest<BootstrapResponse>(getToken, "/api/bootstrap"));
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "読み込みに失敗しました");
    } finally {
      setLoading(false);
    }
  }, [getToken]);

  useEffect(() => { void reload(); }, [reload]);
  useEffect(() => {
    const match = location.pathname.match(/^\/invite\/([^/]+)$/);
    const token = match?.[1];
    if (!bootstrap || bootstrap.user.isMember || !token || invitationState !== "idle") return;
    setInvitationState("accepting");
    void apiRequest<{ ok: boolean }>(getToken, "/api/invitations/accept", {
      method: "POST",
      body: JSON.stringify({ token }),
    }).then(async () => {
      history.replaceState({}, "", "/");
      await reload();
      setInvitationState("idle");
    }).catch((reason: Error) => {
      const messages: Record<string, string> = {
        invitation_expired: "この招待URLは期限切れです。管理者に再発行を依頼してください。",
        invitation_account_mismatch: "この招待は別のアカウント宛てです。指定されたLINEアカウントでログインしてください。",
        invitation_not_available: "この招待URLは使用済み、または取り消されています。",
        invitation_not_found: "招待URLを確認できませんでした。",
      };
      setInvitationError(messages[reason.message] ?? "招待を受諾できませんでした。管理者に確認してください。");
      setInvitationState("error");
    });
  }, [bootstrap, getToken, invitationState, reload]);
  if (loading) return <div className="center-state"><div className="spinner" aria-label="読み込み中" /></div>;
  if (error || !bootstrap) {
    return <div className="center-state"><p className="error-text">{error ?? "ユーザー情報を取得できませんでした"}</p><button className="button" onClick={reload}>再試行</button></div>;
  }
  if (invitationState === "accepting") {
    return <div className="center-state"><div className="spinner" aria-label="招待を確認中" /><p>アカウントを発行しています…</p></div>;
  }
  if (!bootstrap.user.isMember) {
    return <AccessSetup bootstrap={bootstrap} getToken={getToken} onComplete={reload} onSignOut={() => signOut()} invitationError={invitationError} />;
  }

  return <WorkspaceShell bootstrap={bootstrap} getToken={getToken} onSignOut={() => signOut()} />;
}

type NavItem = { key: PageKey; label: string; icon: typeof ChartLine; show: boolean };

function WorkspaceShell({ bootstrap, getToken, onSignOut, initialPage }: {
  bootstrap: BootstrapResponse;
  getToken: () => Promise<string | null>;
  onSignOut: () => void;
  initialPage?: PageKey;
}) {
  const [page, setPage] = useState<PageKey>(initialPage ?? currentPage);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const onPopState = () => setPage(currentPage());
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, []);

  useEffect(() => {
    if (location.pathname !== "/" && !location.pathname.startsWith("/invite/") && requestedPage() !== page) {
      history.replaceState({}, "", pagePath[page]);
    }
  }, [page]);

  function navigate(requested: PageKey) {
    const next = PUBLISHED_PAGES.includes(requested) ? requested : "marketing";
    history.pushState({}, "", pagePath[next]);
    setPage(next);
    setMobileOpen(false);
    window.scrollTo({ top: 0 });
    requestAnimationFrame(() => document.querySelector<HTMLElement>("#main-content h1")?.focus());
  }

  const allowed = (permission: string) => bootstrap.user.permissions.includes(permission) || bootstrap.user.roles.includes("system_admin");
  const canMeasure = allowed("measurement:read");
  // 並びはFigmaのサイドバー（node 628:399）に合わせる。公開していない画面は出さない。
  const allGroups: Array<{ label: string; items: NavItem[] }> = [
    { label: "集客（まめぶろぐ）", items: [
      { key: "marketing", label: "概要", icon: ChartLine, show: canMeasure },
      { key: "measurement", label: "ファネル", icon: Funnel, show: canMeasure },
      { key: "tracking", label: "計測URL", icon: Link2, show: canMeasure },
    ] },
    { label: "LINE", items: [
      { key: "line", label: "連携の状態", icon: MessageCircle, show: allowed("line:read") },
    ] },
  ];
  const groups = allGroups
    .map((group) => ({ ...group, items: group.items.filter((item) => item.show && PUBLISHED_PAGES.includes(item.key)) }))
    .filter((group) => group.items.length > 0);
  const roleLabel = bootstrap.user.roles.includes("system_admin") ? "全社管理者" : bootstrap.user.roles.join(" / ");

  return (
    <div className="app-shell">
      <a className="skip-link" href="#main-content">本文へ移動</a>
      {mobileOpen && <button className="sidebar-scrim" aria-label="メニューを閉じる" onClick={() => setMobileOpen(false)} />}
      <aside className={`sidebar ${mobileOpen ? "sidebar-open" : ""}`} aria-label="メニュー">
        <div className="sidebar-head">
          <Logo className="sidebar-logo" />
          {bootstrap.environment !== "production" && <span className="sidebar-env">{bootstrap.environment === "development" ? "開発環境" : "ローカル"}</span>}
        </div>
        <nav aria-label="メインナビゲーション">
          {groups.map((group) => (
            <div key={group.label} role="group" aria-label={group.label}>
              <p className="nav-group-label">{group.label}</p>
              {group.items.map((item) => (
                <button key={item.key} className={`nav-item ${page === item.key ? "active" : ""}`} onClick={() => navigate(item.key)} aria-current={page === item.key ? "page" : undefined}>
                  <item.icon size={18} strokeWidth={1.8} aria-hidden="true" /><span>{item.label}</span>
                </button>
              ))}
            </div>
          ))}
        </nav>
        <div className="sidebar-footer">
          {/* 全社管理者だけが、illume運営画面にillume管理者として入れる（2026-09-24決定）。 */}
          {bootstrap.user.roles.includes("system_admin") && (
            <a className="nav-item" href="https://illume.design-me.app/" target="_blank" rel="noopener">
              <ExternalLink size={18} strokeWidth={1.8} aria-hidden="true" /><span>illume運営画面</span>
            </a>
          )}
          {allowed("access:manage") && (
            <button className={`nav-item ${page === "access" ? "active" : ""}`} onClick={() => navigate("access")} aria-current={page === "access" ? "page" : undefined}>
              <Users size={18} strokeWidth={1.8} aria-hidden="true" /><span>アカウントと権限</span>
            </button>
          )}
          <div className="user-chip">
            {bootstrap.user.avatarUrl ? <img src={bootstrap.user.avatarUrl} alt="" /> : <span aria-hidden="true">{bootstrap.user.displayName.slice(0, 1)}</span>}
            <div><strong>{bootstrap.user.displayName}</strong><small>{roleLabel}</small></div>
            <button className="sidebar-icon-button" aria-label="ログアウト" title="ログアウト" onClick={onSignOut}><LogOut size={18} aria-hidden="true" /></button>
          </div>
        </div>
      </aside>
      <div className="workspace">
        <header className="mobile-bar">
          <button className="sidebar-icon-button" aria-label={mobileOpen ? "メニューを閉じる" : "メニューを開く"} aria-expanded={mobileOpen} onClick={() => setMobileOpen(!mobileOpen)}>
            {mobileOpen ? <X size={22} aria-hidden="true" /> : <Menu size={22} aria-hidden="true" />}
          </button>
          <Logo className="sidebar-logo" />
          <strong>{pageMeta[page].label}</strong>
        </header>
        <main id="main-content" className="main-content">
          {page === "marketing" && <MarketingOverview getToken={getToken} canWrite={allowed("measurement:write")} />}
          {page === "measurement" && <Measurement getToken={getToken} />}
          {page === "tracking" && <TrackingLinks getToken={getToken} canWrite={allowed("measurement:write")} />}
          {page === "line" && <LineInbox getToken={getToken} currentUserLinked={bootstrap.user.lineLinked} environment={bootstrap.environment} />}
          {page === "finance" && <DomainPage title="売上" description="売上、経費、損益を同じ基準で確認する画面です。" badge="準備中" currentState="要件を整理しています" nextStep="次は、売上と経費の入力元を確定します。" items={[{ title: "月次の売上を確認する", description: "事業とプロジェクトごとに集計します。" }, { title: "経費を登録・確認する", description: "支払先、費目、発生日を記録します。" }, { title: "損益を確認する", description: "売上から原価と経費を差し引いて表示します。" }]} action={<button className="button" onClick={() => navigate("tasks")}>実装タスクを確認する<ListTodo size={15} aria-hidden="true" /></button>} />}
          {page === "illume" && <DomainPage title="illume" description="受講者、講師、教材、学習進捗を扱う画面です。" badge="準備中" currentState="要件を整理しています" nextStep="次は、受講者・講師・教材のデータ構造を確定します。" items={[{ title: "受講者と権限を管理する", description: "管理者、運用者、講師の閲覧範囲を分けます。" }, { title: "教材を管理する", description: "教材、章、課題、公開状態をまとめます。" }, { title: "学習進捗を確認する", description: "受講者の進捗と講師の対応状況を表示します。" }]} action={<button className="button" onClick={() => navigate("tasks")}>実装タスクを確認する<ListTodo size={15} aria-hidden="true" /></button>} />}
          {page === "access" && <AccountRoles getToken={getToken} currentUserId={bootstrap.user.id} />}
          {page === "tasks" && <TaskManagement view="board" getToken={getToken} canWrite={allowed("tasks:write")} onViewChange={(view) => navigate(view === "board" ? "tasks" : "tasksTimeline")} />}
          {page === "tasksTimeline" && <TaskManagement view="timeline" getToken={getToken} canWrite={allowed("tasks:write")} onViewChange={(view) => navigate(view === "board" ? "tasks" : "tasksTimeline")} />}
        </main>
      </div>
    </div>
  );
}

function AccessSetup({ bootstrap, getToken, onComplete, onSignOut, invitationError }: {
  bootstrap: BootstrapResponse;
  getToken: () => Promise<string | null>;
  onComplete: () => Promise<void>;
  onSignOut: () => void;
  invitationError: string | null;
}) {
  const [token, setToken] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  async function submit(event: React.FormEvent) {
    event.preventDefault(); setSaving(true); setError(null);
    try {
      await apiRequest(getToken, "/api/bootstrap-owner", { method: "POST", headers: { "x-bootstrap-token": token } });
      await onComplete();
    } catch { setError("初期管理者キーが一致しません。"); setSaving(false); }
  }
  return (
    <main className="access-setup-page">
      <header className="login-masthead"><Logo className="login-logo" /><span>INTERNAL USE ONLY</span></header>
      <section className="access-setup-region" aria-labelledby="setup-title">
        <div className="access-setup-card">
          <p className="login-step">ACCESS / 02</p>
          <h1 id="setup-title">{bootstrap.ownerBootstrapAvailable ? "管理者アクセスを設定" : "このアカウントではアクセスできません"}</h1>
          <p className="muted">{bootstrap.ownerBootstrapAvailable
            ? "この環境には管理者がまだ登録されていません。初期管理者キーを入力してください。"
            : "管理者から届いた招待URLを開くか、アクセス権の付与を依頼してください。"}</p>
          <div className="signed-in-account">
            {bootstrap.user.avatarUrl ? <img src={bootstrap.user.avatarUrl} alt="" /> : <span>{bootstrap.user.displayName.slice(0, 1)}</span>}
            <div><small>ログイン中のアカウント</small><strong>{bootstrap.user.displayName}</strong></div>
          </div>
          {invitationError && <p className="error-banner" role="alert">{invitationError}</p>}
          {bootstrap.ownerBootstrapAvailable ? (
            <form onSubmit={submit} className="access-setup-form">
              <div className="form-field">
                <label htmlFor="bootstrap-token">初期管理者キー</label>
                <p id="bootstrap-token-help">Cloudflareに登録した初期設定用のキーです。</p>
                <input id="bootstrap-token" className="input access-key-input" type="password" value={token} onChange={(event) => setToken(event.target.value)} autoComplete="off" spellCheck={false} aria-invalid={Boolean(error)} aria-describedby={`bootstrap-token-help${error ? " bootstrap-token-error" : ""}`} required />
              </div>
              <button className="button button-primary button-wide" disabled={saving}>{saving ? "設定中…" : "管理者として開始"}</button>
              {error && <p id="bootstrap-token-error" className="error-text" role="alert">{error}</p>}
            </form>
          ) : <p className="notice">アクセス権を付与したあと、画面を再読み込みしてください。</p>}
          <button className="button button-quiet" onClick={onSignOut}>別のアカウントでログイン</button>
        </div>
      </section>
      <footer className="login-footer"><span>design me</span><span>SECURE ACCESS</span></footer>
    </main>
  );
}

function PageHeader({ eyebrow, title, description, action }: { eyebrow?: string; title: string; description: string; action?: React.ReactNode }) {
  return <header className="page-header"><div>{eyebrow && <p className="eyebrow">{eyebrow}</p>}<h1 tabIndex={-1}>{title}</h1><p className="muted">{description}</p></div>{action && <div className="page-action">{action}</div>}</header>;
}

function DomainPage({ title, description, badge, currentState, nextStep, items, action }: {
  title: string;
  description: string;
  badge: string;
  currentState: string;
  nextStep: string;
  items: Array<{ title: string; description: string }>;
  action?: React.ReactNode;
}) {
  return <>
    <PageHeader title={title} description={description} />
    <section className="domain-panel" aria-labelledby="domain-state-title">
      <div className="domain-state">
        <span className="domain-badge">{badge}</span>
        <div><p id="domain-state-title">現在の状態</p><strong>{currentState}</strong><small>{nextStep}</small></div>
      </div>
      <div className="domain-plan">
        <div className="domain-plan-head"><h2>追加予定の機能</h2><p>実装範囲が決まり次第、順番を確定します。</p></div>
        <ol className="domain-list">
          {items.map((item, index) => <li key={item.title}><span>{String(index + 1).padStart(2, "0")}</span><div><strong>{item.title}</strong><p>{item.description}</p></div><small>予定</small></li>)}
        </ol>
      </div>
      {action && <div className="domain-action">{action}</div>}
    </section>
  </>;
}

const LOCAL_PREVIEW_BOOTSTRAP: BootstrapResponse = {
  user: {
    id: "local-preview",
    displayName: "ローカル確認",
    email: null,
    avatarUrl: null,
    permissions: [],
    roles: ["system_admin"],
    isMember: true,
    lineLinked: true,
  },
  ownerBootstrapAvailable: false,
  environment: "local",
};

function LocalPreview({ initialPage }: { initialPage: PageKey }) {
  return (
    <WorkspaceShell
      bootstrap={LOCAL_PREVIEW_BOOTSTRAP}
      getToken={async () => null}
      onSignOut={() => undefined}
      initialPage={initialPage}
    />
  );
}

function monthRange() {
  const now = new Date();
  const local = (date: Date) => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
  return { from: local(new Date(now.getFullYear(), now.getMonth(), 1)), to: local(now) };
}

function useFunnel(getToken: () => Promise<string | null>, from: string, to: string, enabled = true) {
  const [rows, setRows] = useState<FunnelRow[]>([]);
  const [loading, setLoading] = useState(enabled);
  const [error, setError] = useState<string | null>(null);
  const load = useCallback(async () => {
    if (!enabled) { setLoading(false); return; }
    setLoading(true); setError(null);
    try {
      const response = await apiRequest<{ rows: FunnelRow[] }>(getToken, `/api/measurement/daily?from=${from}&to=${to}`);
      setRows(response.rows ?? []);
    } catch { setError("計測データを取得できませんでした。"); }
    finally { setLoading(false); }
  }, [enabled, from, getToken, to]);
  useEffect(() => { void load(); }, [load]);
  return { rows, loading, error, load };
}

function MetricCard({ label, value, note, accent = false }: { label: string; value: number | string; note: string; accent?: boolean }) {
  return <article className={`metric-card ${accent ? "metric-accent" : ""}`}><div className="metric-card-head"><p className="metric-label">{label}</p>{accent && <span>重点KPI</span>}</div><strong>{typeof value === "number" ? value.toLocaleString() : value}</strong><p>{note}</p></article>;
}

function Measurement({ getToken }: { getToken: () => Promise<string | null> }) {
  const initial = monthRange();
  const [from, setFrom] = useState(initial.from); const [to, setTo] = useState(initial.to);
  const { rows, loading, error, load } = useFunnel(getToken, from, to);
  const metrics = [
    ["計測URLクリック", "tracking_link_clicks"], ["ブログ到達", "blog_landings"],
    ["ブログ→LP", "blog_to_lp_clicks"], ["LP閲覧", "lp_views"],
    ["LINE CTA", "line_cta_clicks"], ["LINE流入", "line_first_follows"],
  ] as const;
  const grouped = useMemo(() => {
    const map = new Map<string, FunnelRow>();
    for (const row of rows) {
      const existing = map.get(row.report_date) ?? { ...row, source_channel: "all", route_key: "all", tracking_link_clicks: 0, blog_landings: 0, blog_to_lp_clicks: 0, lp_views: 0, line_cta_clicks: 0, line_liff_attributions: 0, line_first_follows: 0 };
      for (const [, key] of metrics) existing[key] += Number(row[key] ?? 0);
      existing.line_liff_attributions += Number(row.line_liff_attributions ?? 0);
      map.set(row.report_date, existing);
    }
    return [...map.values()].sort((a, b) => b.report_date.localeCompare(a.report_date));
  }, [rows]);
  return <>
    <PageHeader title="ファネル" description="期間と流入経路を固定し、日ごとの正しい数字と離脱率を確認します。" action={<button className="button" onClick={load}><RefreshCw size={16} />更新</button>} />
    <div className="filter-bar"><label>開始日<input className="input" type="date" value={from} onChange={(e) => setFrom(e.target.value)} /></label><label>終了日<input className="input" type="date" value={to} onChange={(e) => setTo(e.target.value)} /></label></div>
    {error && <p className="error-banner">{error}</p>}
    <section className="funnel-grid" aria-label="ファネル合計">
      {metrics.map(([label, key], index) => {
        const value = total(rows, key); const previous = index > 0 ? total(rows, metrics[index - 1][1]) : 0;
        return <article key={key}><span className="funnel-index">{String(index + 1).padStart(2, "0")}</span><p>{label}</p><strong>{loading ? "—" : value.toLocaleString()}</strong><small>{index === 0 ? "期間合計" : `前段階比 ${percent(value, previous)}`}</small></article>;
      })}
    </section>
    <section className="panel"><div className="panel-head"><div><h2>日別ファネル</h2><p className="muted">数値は重複排除済みの利用者単位です。</p></div></div>
      <div className="table-wrap"><table><thead><tr><th>日付</th>{metrics.map(([label]) => <th key={label}>{label}</th>)}</tr></thead><tbody>
        {grouped.map((row) => <tr key={row.report_date}><th>{row.report_date}</th>{metrics.map(([, key]) => <td key={key}>{Number(row[key]).toLocaleString()}</td>)}</tr>)}
        {!loading && grouped.length === 0 && <tr><td colSpan={7} className="empty-cell">この期間の本番計測データはまだありません。</td></tr>}
      </tbody></table></div>
    </section>
    <section className="panel"><div className="panel-head"><div><h2>流入元・経路別</h2><p className="muted">SEO、SNS、投稿・記事ごとの差を確認します。</p></div></div>
      <div className="table-wrap"><table><thead><tr><th>日付</th><th>媒体</th><th>経路</th><th>LP</th><th>CTA</th><th>LINE流入</th></tr></thead><tbody>{rows.map((row, index) => <tr key={`${row.report_date}-${row.source_channel}-${row.route_key}-${index}`}><td>{row.report_date}</td><td><span className="tag">{row.source_channel}</span></td><td>{row.route_key}</td><td>{row.lp_views}</td><td>{row.line_cta_clicks}</td><td>{row.line_first_follows}</td></tr>)}</tbody></table></div>
    </section>
  </>;
}

const emptyTrackingForm = { slug: "", labelJa: "", sourceChannel: "seo", sourceUrl: "", placementKey: "article-cta", routeKey: "blog-to-lp", destinationUrl: "", utmSource: "", utmMedium: "", utmCampaign: "", utmContent: "" };

function TrackingLinks({ getToken, canWrite }: { getToken: () => Promise<string | null>; canWrite: boolean }) {
  const [items, setItems] = useState<TrackingLink[]>([]); const [loading, setLoading] = useState(true); const [open, setOpen] = useState(false); const [form, setForm] = useState(emptyTrackingForm); const [error, setError] = useState<string | null>(null);
  const load = useCallback(async () => { setLoading(true); try { const response = await apiRequest<{ items: TrackingLink[] }>(getToken, "/api/measurement/tracking-links"); setItems(response.items ?? []); } catch { setError("計測URLを取得できませんでした。"); } finally { setLoading(false); } }, [getToken]);
  useEffect(() => { void load(); }, [load]);
  async function create(event: React.FormEvent) { event.preventDefault(); setError(null); try { await apiRequest(getToken, "/api/measurement/tracking-links", { method: "POST", body: JSON.stringify(form) }); setForm(emptyTrackingForm); setOpen(false); await load(); } catch { setError("入力内容を確認してください。slugの重複やURL形式が原因の可能性があります。"); } }
  async function archive(id: string) { if (!confirm("この計測URLをアーカイブしますか？ 過去データは残ります。")) return; await apiRequest(getToken, `/api/measurement/tracking-links/${id}`, { method: "DELETE" }); await load(); }
  return <>
    <PageHeader title="計測URL" description="日本語の説明と実際のURLを対応させ、追加・停止・削除をここで管理します。" action={canWrite ? <button className="button button-primary" onClick={() => setOpen(!open)}><Plus size={17} />新規発行</button> : undefined} />
    {open && <section className="panel"><div className="panel-head"><div><h2>計測URLを発行</h2><p className="muted">投稿・記事と設置場所を一意にします。</p></div></div><form className="form-grid" onSubmit={create}>
      <label>管理名<input className="input" value={form.labelJa} onChange={(e) => setForm({ ...form, labelJa: e.target.value })} placeholder="デザインスクール比較記事 CTA" required /></label>
      <label>slug<input className="input" value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} placeholder="school-compare-cta" pattern="[a-z0-9-]+" required /></label>
      <label>流入媒体<select className="input" value={form.sourceChannel} onChange={(e) => setForm({ ...form, sourceChannel: e.target.value })}><option value="seo">SEO</option><option value="x">X</option><option value="threads">Threads</option><option value="instagram">Instagram</option><option value="direct">直接</option></select></label>
      <label>設置場所<input className="input" value={form.placementKey} onChange={(e) => setForm({ ...form, placementKey: e.target.value })} placeholder="article-footer-cta" required /></label>
      <label>経路キー<input className="input" value={form.routeKey} onChange={(e) => setForm({ ...form, routeKey: e.target.value })} placeholder="blog-to-lp" required /></label>
      <label>元投稿・記事URL<input className="input" type="url" value={form.sourceUrl} onChange={(e) => setForm({ ...form, sourceUrl: e.target.value })} placeholder="https://webdesign-mame.com/..." /></label>
      <label className="span-2">遷移先URL<input className="input" type="url" value={form.destinationUrl} onChange={(e) => setForm({ ...form, destinationUrl: e.target.value })} placeholder="https://..." required /></label>
      <div className="form-actions span-2"><button type="button" className="button" onClick={() => setOpen(false)}>キャンセル</button><button className="button button-primary">発行する</button></div>
    </form></section>}
    {error && <p className="error-banner">{error}</p>}
    <section className="panel"><div className="panel-head"><div><h2>登録済みURL</h2><p className="muted">{loading ? "読み込み中" : `${items.length}件`}</p></div><button className="icon-button" aria-label="更新" onClick={load}><RefreshCw size={17} /></button></div>
      <div className="table-wrap"><table><thead><tr><th>説明</th><th>媒体</th><th>設置場所</th><th>経路</th><th>計測URL</th><th>状態</th><th /></tr></thead><tbody>{items.map((item) => { const url = `https://mame-measurement-api.msys.workers.dev/go/${item.slug}`; return <tr key={item.id}><td><strong>{item.label_ja}</strong>{item.source_url && <a className="sub-link" href={item.source_url} target="_blank" rel="noreferrer">元URL <ExternalLink size={12} /></a>}</td><td><span className="tag">{item.source_channel}</span></td><td>{item.placement_key}</td><td>{item.route_key}</td><td><button className="copy-button" onClick={() => navigator.clipboard.writeText(url)} title={url}><Copy size={14} />コピー</button></td><td><span className={`status status-${item.status}`}>{item.status}</span></td><td>{canWrite && <button className="text-button danger" onClick={() => archive(item.id)}>削除</button>}</td></tr>; })}</tbody></table></div>
    </section>
  </>;
}

function LineInbox({ getToken, currentUserLinked, environment }: {
  getToken: () => Promise<string | null>;
  currentUserLinked: boolean;
  environment: string;
}) {
  const [messages, setMessages] = useState<LineMessage[]>([]); const [stats, setStats] = useState<{ contacts: Array<{ friend_status: string; count: number }>; inbound: { count: number }; outbound: { count: number } } | null>(null); const [loading, setLoading] = useState(true); const [error, setError] = useState<string | null>(null);
  const load = useCallback(async () => { setLoading(true); setError(null); try { const [messageData, statusData] = await Promise.all([apiRequest<{ rows: LineMessage[] }>(getToken, "/api/line/messages"), apiRequest<typeof stats>(getToken, "/api/line/status")]); setMessages(messageData.rows ?? []); setStats(statusData); } catch { setError("LINEの連携状態を取得できませんでした。"); } finally { setLoading(false); } }, [getToken]);
  useEffect(() => { void load(); }, [load]);
  const following = stats?.contacts.find((item) => item.friend_status === "following")?.count ?? 0;
  const webhookUrl = `${location.origin}/api/line/webhook`;
  return <>
    <PageHeader title="LINEとの連携の状態" description="Webhook、流入計測、特典送付、受信履歴を同じLINEユーザーに結び付けます。" action={<button className="button" onClick={load}><RefreshCw size={16} />同期</button>} />
    {error && <p className="error-banner">{error}</p>}
    <section className="panel line-connection-panel">
      <div className="panel-head"><div><h2>LINEとの役割分担</h2><p className="muted">配信運用と計測基盤を分け、二重管理を避けます。</p></div><span className="status status-active">Webhook実装済み</span></div>
      <div className="line-role-map">
        <div><strong>この管理画面</strong><p>友だち追加・流入元の計測、Webhook受信、特典送付、イベント履歴を確認します。</p></div>
        <div><strong>LINE Official Account Manager</strong><p>ステップ配信、個別返信、配信コンテンツの運用は公式画面で行います。</p></div>
      </div>
      <div className="webhook-endpoint">
        <div><span>この{environment}環境の受信URL</span><code>{webhookUrl}</code></div>
        <button className="copy-button" onClick={() => navigator.clipboard.writeText(webhookUrl)}><Copy size={14} />コピー</button>
      </div>
      <p className="endpoint-note">LINE公式アカウント側で現在このURLが選択されているかは、Messaging API設定画面で確認します。</p>
    </section>
    <section className="metric-grid"><MetricCard label="友だち状態：有効" value={following} note="Webhook受信済み" /><MetricCard label="受信メッセージ" value={stats?.inbound?.count ?? 0} note="テキスト受信数" /><MetricCard label="送信メッセージ" value={stats?.outbound?.count ?? 0} note="通知・お知らせ" /><MetricCard label="自分のLINE ID" value={currentUserLinked ? "同期済み" : "未同期"} note="Clerk LINE認証との紐付け" accent={currentUserLinked} /></section>
    <section className="panel"><div className="panel-head"><div><h2>メッセージ履歴</h2><p className="muted">LINE公式アカウントのWebhookから受信します。</p></div></div>
      <div className="message-list">{messages.map((message) => <article key={message.id} className="message-row">{message.picture_url ? <img src={message.picture_url} alt="" /> : <div className="avatar-fallback"><MessageCircle size={17} /></div>}<div><div className="message-meta"><strong>{message.display_name ?? "LINEユーザー"}</strong><span className={`tag ${message.direction === "outbound" ? "tag-blue" : ""}`}>{message.direction === "inbound" ? "受信" : "送信"}</span><time>{new Date(`${message.created_at}Z`).toLocaleString("ja-JP")}</time></div><p>{message.body}</p></div></article>)}{!loading && messages.length === 0 && <p className="empty-state">Webhook受信後、ここにメッセージが表示されます。</p>}</div>
    </section>
  </>;
}

export { LocalPreview };
export default App;
